import React, { useState, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Modal from '../../../../components/Modal';
import SearchableClientDropdown from '../../../../components/SearchableClientDropdown';
import './styles.scss';

interface AddContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactCreated: () => void;
  preSelectedClientId?: number | null;
}

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  client_id: string;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  client_id?: string;
  general?: string;
  newClient?: string;
}

const AddContactModal: React.FC<AddContactModalProps> = ({
  isOpen,
  onClose,
  onContactCreated,
  preSelectedClientId
}) => {
  const supabase = useSupabaseClient();

  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    client_id: preSelectedClientId ? preSelectedClientId.toString() : ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isAddingClient, setIsAddingClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleInputChange = useCallback((field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handleCreateClient = useCallback(async () => {
    if (!newClientName.trim()) {
      setErrors(prev => ({ ...prev, newClient: 'Client name is required' }));
      return;
    }

    setIsCreatingClient(true);
    setErrors(prev => ({ ...prev, newClient: undefined }));

    try {
      const { data, error } = await supabase
        .from('clients')
        .insert({ company_name: newClientName.trim() })
        .select()
        .single();

      if (error) throw error;

      handleInputChange('client_id', data.id.toString());
      setRefreshKey(prev => prev + 1);
      setIsAddingClient(false);
      setNewClientName('');
    } catch (err) {
      console.error('Error creating client:', err);
      setErrors(prev => ({
        ...prev,
        newClient: err instanceof Error ? err.message : 'Failed to create client'
      }));
    } finally {
      setIsCreatingClient(false);
    }
  }, [newClientName, handleInputChange, supabase]);

  const handleCancelAddClient = useCallback(() => {
    setIsAddingClient(false);
    setNewClientName('');
    setErrors(prev => ({ ...prev, newClient: undefined }));
  }, []);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.client_id) {
      newErrors.client_id = 'Client is required';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const { error } = await supabase
        .from('contacts')
        .insert({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
          client_id: parseInt(formData.client_id, 10)
        });

      if (error) throw error;

      onContactCreated();
      handleClose();
    } catch (err) {
      console.error('Error creating contact:', err);
      setErrors({
        general: err instanceof Error ? err.message : 'Failed to create contact'
      });
    } finally {
      setIsSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, supabase, onContactCreated]);

  const handleClose = useCallback(() => {
    setFormData({ first_name: '', last_name: '', email: '', phone: '', client_id: '' });
    setErrors({});
    setIsAddingClient(false);
    setNewClientName('');
    onClose();
  }, [onClose]);

  const footerContent = (
    <div className="modal-footer-actions">
      <button
        type="button"
        className="btn-secondary"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Creating...' : 'Create Contact'}
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Contact"
      size="md"
      hasFooter={true}
      footerContent={footerContent}
    >
      <div className="form-content add-contact-form">
        {errors.general && (
          <div className="error-message general-error">{errors.general}</div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">
              First Name <span className="required">*</span>
            </label>
            <input
              id="first_name"
              type="text"
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              placeholder="First name"
              className={errors.first_name ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.first_name && <div className="error-message">{errors.first_name}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="last_name">
              Last Name <span className="required">*</span>
            </label>
            <input
              id="last_name"
              type="text"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              placeholder="Last name"
              className={errors.last_name ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.last_name && <div className="error-message">{errors.last_name}</div>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="email@example.com"
            className={errors.email ? 'error' : ''}
            disabled={isSubmitting}
          />
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone</label>
          <input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="Phone number"
            disabled={isSubmitting}
          />
        </div>

        <div className="form-group">
          <div className="form-group-header">
            <label htmlFor="client">
              Client <span className="required">*</span>
            </label>
            {!isAddingClient && (
              <button
                type="button"
                className="btn-add-client"
                onClick={() => setIsAddingClient(true)}
              >
                + add client
              </button>
            )}
          </div>

          {isAddingClient ? (
            <div className="add-client-form">
              <div className="add-client-input-group">
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => {
                    setNewClientName(e.target.value);
                    if (errors.newClient) {
                      setErrors(prev => ({ ...prev, newClient: undefined }));
                    }
                  }}
                  placeholder="Enter client name"
                  className={`form-input ${errors.newClient ? 'error' : ''}`}
                  disabled={isCreatingClient}
                />
                <div className="add-client-actions">
                  <button
                    type="button"
                    onClick={handleCreateClient}
                    disabled={isCreatingClient || !newClientName.trim()}
                    className="btn-primary btn-small"
                  >
                    {isCreatingClient ? 'Creating...' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelAddClient}
                    disabled={isCreatingClient}
                    className="btn-secondary btn-small"
                  >
                    Cancel
                  </button>
                </div>
              </div>
              {errors.newClient && <div className="error-message">{errors.newClient}</div>}
            </div>
          ) : (
            <SearchableClientDropdown
              key={refreshKey}
              value={formData.client_id || null}
              onChange={(clientId) => {
                handleInputChange('client_id', clientId ? clientId.toString() : '');
              }}
              placeholder="Search and select client"
              allowNone={false}
              required={true}
            />
          )}
          {errors.client_id && !isAddingClient && (
            <div className="error-message">{errors.client_id}</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddContactModal;
